// What /receptora keeps in its URL: the tab it opens on and the connection list's filters and page,
// so a reload or a shared link opens the same list; and the status chips, each one set of statuses.
import { Array as Arr, Option, Schema, String as Str } from 'effect';
import {
  OPIN_RECEIVER_CONNECTION_STATUSES,
  OpinReceiverConnectionStatus,
} from '@finnest/power-admin-contracts/opin-receiver';
import type { OpinReceiverStatusCounts } from '@finnest/power-admin-contracts/opin-receiver';
import type { ConnectionFilters } from './client/calls.ts';

export const RECEPTORA_TABS = ['conexoes', 'consentimentos'] as const;

export const ReceptoraTab = Schema.Literals(RECEPTORA_TABS);
export type ReceptoraTab = typeof ReceptoraTab.Type;

const Page = Schema.Int.check(Schema.isGreaterThanOrEqualTo(1));
const Statuses = Schema.Array(OpinReceiverConnectionStatus);

export const ReceptoraSearch = Schema.Struct({
  aba: Schema.optionalKey(ReceptoraTab),
  clientId: Schema.optionalKey(Schema.String),
  status: Schema.optionalKey(Statuses),
  q: Schema.optionalKey(Schema.String),
  page: Schema.optionalKey(Page),
});
export type ReceptoraSearch = typeof ReceptoraSearch.Type;

// The same URL, each key read as present or not: encoding a filter that is `None` leaves its key
// out, so the URL names only the filters that narrow the list.
const ReceptoraUrl = Schema.Struct({
  aba: Schema.OptionFromOptionalKey(ReceptoraTab),
  clientId: Schema.OptionFromOptionalKey(Schema.String),
  status: Schema.OptionFromOptionalKey(Statuses),
  q: Schema.OptionFromOptionalKey(Schema.String),
  page: Schema.OptionFromOptionalKey(Page),
});
const encodeUrl = Schema.encodeSync(ReceptoraUrl);

const FIRST_PAGE = 1;

/** The connection list's filters a URL states; what it leaves out asks for every connection. */
export const connectionFiltersOf = (search: ReceptoraSearch): ConnectionFilters => ({
  clientId: search.clientId ?? '',
  statuses: search.status ?? [],
  search: search.q ?? '',
  page: search.page ?? FIRST_PAGE,
});

/** The URL of the Conexões tab showing `filters`, naming only the filters that narrow the list. */
export const receptoraSearchOf = (
  aba: Option.Option<ReceptoraTab>,
  { clientId, statuses, search, page }: ConnectionFilters,
): ReceptoraSearch =>
  encodeUrl({
    aba,
    clientId: Option.liftPredicate(clientId, Str.isNonEmpty),
    status: Option.liftPredicate(statuses, Arr.isReadonlyArrayNonEmpty),
    q: Option.liftPredicate(search, Str.isNonEmpty),
    page: Option.liftPredicate(page, (asked) => asked !== FIRST_PAGE),
  });

/** The list's status chips: every connection, or the statuses one situation gathers. */
export const STATUS_CHIPS = ['all', 'active', 'attention', 'progress', 'ended'] as const;
export type StatusChip = (typeof STATUS_CHIPS)[number];

/** How each chip reads. */
export const STATUS_CHIP_LABELS = {
  all: 'Todas',
  active: 'Ativas',
  attention: 'Pedem atenção',
  progress: 'Em andamento',
  ended: 'Encerradas',
} as const satisfies Record<StatusChip, string>;

// The statuses each chip puts in the URL, in the contract's order; Todas puts none.
const CHIP_STATUSES = {
  all: [],
  active: ['active'],
  attention: ['error', 'revocation_pending'],
  progress: ['pending', 'awaiting_auth', 'syncing'],
  ended: ['revoked', 'expired'],
} as const satisfies Record<StatusChip, readonly OpinReceiverConnectionStatus[]>;

// The statuses each chip counts: Todas counts every one.
const CHIP_COUNTED: Readonly<Record<StatusChip, readonly OpinReceiverConnectionStatus[]>> = {
  ...CHIP_STATUSES,
  all: OPIN_RECEIVER_CONNECTION_STATUSES,
};

/** The statuses `chip` filters the list by. */
export const chipStatuses = (chip: StatusChip): readonly OpinReceiverConnectionStatus[] =>
  CHIP_STATUSES[chip];

/** Whether `statuses` are exactly the ones `chip` sets, so the chip shows as pressed. */
export const isChipPressed = (
  chip: StatusChip,
  statuses: readonly OpinReceiverConnectionStatus[],
): boolean => {
  const own: readonly OpinReceiverConnectionStatus[] = CHIP_STATUSES[chip];
  return own.length === new Set(statuses).size && statuses.every((status) => own.includes(status));
};

/** How many connections a chip gathers, from the list's per-status counts. */
export const chipCount = (chip: StatusChip, counts: OpinReceiverStatusCounts): number =>
  CHIP_COUNTED[chip].reduce((total, status) => total + counts[status], 0);
